import React, { useState } from 'react';
import { User, QrCode, Download, CheckCircle } from 'lucide-react';
import './GuestRegistration.css';

interface Person {
  id: number;
  name: string;
  totalSpent: number;
  totalSavings: number;
  purchaseCount: number;
  createdAt: string;
  qrCode?: string;
  qrCodeData?: string;
}

interface GuestRegistrationProps {
  onRegistrationSuccess: (person: Person) => void;
}

const GuestRegistration: React.FC<GuestRegistrationProps> = ({ onRegistrationSuccess }) => {
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registeredPerson, setRegisteredPerson] = useState<Person | null>(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Bitte gib einen Namen ein');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('https://boersensaufen.fwne.de:1234/api/persons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: name.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registrierung fehlgeschlagen');
      }

      const newPerson = await response.json();
      
      // Save to localStorage for recognition
      localStorage.setItem('guestId', newPerson.id.toString());
      localStorage.setItem('guestName', newPerson.name);
      
      setRegisteredPerson(newPerson);
      onRegistrationSuccess(newPerson);
    } catch (error) {
      console.error('Registration error:', error);
      setError(error instanceof Error ? error.message : 'Registrierung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (registeredPerson?.qrCode) {
      const link = document.createElement('a');
      link.download = `${registeredPerson.name}-qr-code.png`;
      link.href = registeredPerson.qrCode;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleNewRegistration = () => {
    setRegisteredPerson(null);
    setName('');
    setError('');
  };

  if (registeredPerson) {
    return (
      <div className="guest-registration">
        <div className="success-state">
          <div className="success-header">
            <CheckCircle className="success-icon" />
            <h2>Willkommen, {registeredPerson.name}!</h2>
            <p>Du bist jetzt registriert und kannst den "Mein Bereich" Tab nutzen</p>
          </div>

          {registeredPerson.qrCode && (
            <div className="qr-section">
              <div className="qr-code-container">
                <img 
                  src={registeredPerson.qrCode} 
                  alt="Dein QR-Code" 
                  className="qr-code-image"
                />
                <p>Dein persönlicher QR-Code</p>
                <button onClick={downloadQRCode} className="download-btn">
                  <Download size={16} />
                  QR-Code speichern
                </button>
              </div>

              <div className="qr-instructions">
                <h3>So funktioniert's:</h3>
                <ul>
                  <li>📱 Screenshot vom QR-Code machen oder downloaden</li>
                  <li>🍺 QR-Code an der Bar vorzeigen</li>
                  <li>⚡ Schnelle Bezahlung ohne Namen zu nennen</li>
                  <li>📊 Alle deine Käufe werden automatisch getrackt</li>
                </ul>
              </div>
            </div>
          )}

          <div className="success-actions">
            <button 
              onClick={handleNewRegistration}
              className="secondary-btn"
            >
              Weitere Person registrieren
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-registration">
      <div className="form-section">
        <div className="form-header">
          <QrCode className="form-icon" />
          <h2>Gäste-Registrierung</h2>
          <p>Registriere dich für deinen persönlichen QR-Code und tracke deine Ausgaben</p>
        </div>

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="input-group">
            <label htmlFor="name">Dein Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Max Mustermann"
              disabled={isLoading}
              maxLength={50}
              className="name-input"
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            disabled={isLoading || !name.trim()}
            className="register-btn"
          >
            {isLoading ? (
              <>Registriere...</>
            ) : (
              <>
                <User size={16} />
                Jetzt registrieren
              </>
            )}
          </button>
        </form>

        <div className="benefits">
          <h3>Deine Vorteile:</h3>
          <ul>
            <li>⚡ Schnelle Bezahlung mit QR-Code</li>
            <li>📊 Verfolge deine Ausgaben in Echtzeit</li>
            <li>💰 Sieh deine Ersparnisse und Verluste</li>
            <li>🏆 Vergleiche dich im Ranking</li>
            <li>📱 Alle Daten bleiben auf deinem Gerät gespeichert</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GuestRegistration;